/**
 * The DSCR sizer's Review-mode content: the KPI strip a pricing desk reads
 * first (exit price, coupon, DSCR, loan amount, LTV, eligibility) and the
 * result cards flowed wide.
 *
 * Render-only, like DscrResultsColumn: every figure is a field on the STORED
 * response for the version on screen (`result.result`, `result.leverage`,
 * `result.inputs`, the ladder's own `passing` / `clamped` flags). The cap and
 * headroom are read off the ladder's clamped rungs exactly as the column's
 * exit-price card reads them — nothing is priced here.
 */
import { fmtMoney, fmtPct, fmtRate, fmtRatio } from "./sizerFormat";
import { KpiCell, KpiHero, KpiStrip } from "./workspace/ScenarioReview";
import DscrResultsColumn, {
  clampBounds,
  dscrEligibility,
  fmtExit,
  fmtPts,
} from "./DscrResultsColumn";

const ANCHOR_CAPTION = {
  best: "Best execution",
  ladder_mid: "No rung passed — indicative",
  guideline: "Fails the guidelines — indicative",
};

export function DscrKpis({ result, scenario, version }) {
  const res = result?.result || {};
  const s = scenario || {};
  const inputs = result?.inputs || {};
  const lev = result?.leverage || {};
  const bounds = clampBounds(result?.ladder);
  const elig = dscrEligibility(result);
  const guideline = result?.guidelineExceptions || [];
  const hard = guideline.filter((e) => e.severity === "hard").length;
  const soft = guideline.length - hard;
  const maxLoan =
    result?.suggestions?.maxLoanAtMaxLeverage ??
    lev.maxLoanAtMaxLeverage ??
    null;
  const loanAmount = inputs.requestedLoanAmount ?? s.requestedLoanAmount;
  const requested = inputs.requestedCoupon ?? s.requestedCoupon;
  const headroom =
    bounds.cap != null && res.price != null
      ? (bounds.cap - res.price) * 100
      : null;

  const chips = [
    { k: "Coupon", v: fmtRate(res.coupon), on: res.coupon != null },
    {
      k: "Net pts",
      v: fmtPts(res.premiumDiscountPts, 3),
      on: res.premiumDiscountPts != null,
    },
    { k: "Tier", v: res.tier, on: Boolean(res.tier) },
    {
      k: "Margin",
      v: `−${Number(res.marginPointsApplied).toFixed(3)}`,
      on: res.marginPointsApplied != null,
    },
  ].filter((c) => c.on);

  return (
    <KpiStrip
      hero={
        <KpiHero
          label={`Exit price${version ? ` · v${version}` : ""}`}
          value={fmtExit(res.price)}
          sub={
            bounds.cap != null
              ? `Cap ${fmtExit(bounds.cap)}${
                  headroom != null
                    ? ` · headroom ${headroom.toFixed(2)} pts`
                    : ""
                }`
              : null
          }
          chips={chips}
        />
      }
    >
      <KpiCell
        label="Coupon"
        value={fmtRate(res.coupon)}
        caption={
          res.anchor === "requested" && requested != null && requested !== ""
            ? `Requested ${fmtRate(requested)}`
            : ANCHOR_CAPTION[res.anchor] || null
        }
      />
      <KpiCell
        label="DSCR"
        value={fmtRatio(res.dscr, 3)}
        tone={res.dscr != null && Number(res.dscr) < 1 ? "bad" : null}
        caption={res.pitia != null ? `PITIA ${fmtMoney(res.pitia)} /mo` : null}
      />
      <KpiCell
        label="Loan amount"
        value={fmtMoney(loanAmount)}
        caption={
          (lev.overLtv || lev.overLtc) && maxLoan != null
            ? `Max ~${fmtMoney(maxLoan)}`
            : s.loanPurpose || null
        }
      />
      <KpiCell
        label="LTV"
        value={fmtPct(res.ltv, 2)}
        tone={lev.overLtv ? "bad" : null}
        caption={lev.maxLtv != null ? `Max ${fmtPct(lev.maxLtv, 0)}` : null}
      />
      <KpiCell
        label="Eligibility"
        value={elig.passing ? "Eligible" : "Ineligible"}
        tone={elig.passing ? "good" : "bad"}
        caption={
          hard
            ? `${hard} guideline fail${hard === 1 ? "" : "s"}`
            : soft
              ? `${soft} exception${soft === 1 ? "" : "s"} to request`
              : !elig.passing
                ? ANCHOR_CAPTION[elig.anchor] || null
                : null
        }
      />
    </KpiStrip>
  );
}

/** The result cards, flowed into columns at page width; the exit-price card
 *  is the KPI strip's hero, so it is dropped here. Read-only: no re-anchor. */
export const DscrPanels = ({ result, zip }) => (
  <DscrResultsColumn result={result} zip={zip} masonry hideHero />
);
